import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import { fetchLabUtilizationRanking } from "../../../API_Service/Dashboard_Slice";
import { companyID } from "../../../Common/UIComponents";
import { useDashboardSignalR } from "../../..//Common/useDashboardSignalR";

const BAR_COLORS = [
  "#33b2df",
  "#546E7A",
  "#d4526e",
  "#13d8aa",
  "#A5978B",
  "#2b908f",
  "#f9a3a4",
  "#90ee7e",
  "#f48024",
  "#69d2e7",
];

const MONTHS = [
  { label: "January", value: 0 },
  { label: "February", value: 1 },
  { label: "March", value: 2 },
  { label: "April", value: 3 },
  { label: "May", value: 4 },
  { label: "June", value: 5 },
  { label: "July", value: 6 },
  { label: "August", value: 7 },
  { label: "September", value: 8 },
  { label: "October", value: 9 },
  { label: "November", value: 10 },
  { label: "December", value: 11 },
];

function getYearOptions(span = 5) {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i < span; i++) years.push(currentYear - i);
  return years;
}

function getMonthDateRange(month, year) {
  const today = new Date();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);

  const isCurrentMonth =
    year === today.getFullYear() && month === today.getMonth();

  const fromDate = first.toISOString().split("T")[0];
  const toDate = (isCurrentMonth ? today : last).toISOString().split("T")[0];

  return { fromDate, toDate };
}

export default function LabUtilizationRanking({ companyId = companyID }) {
  const dispatch = useDispatch();

  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const yearOptions = getYearOptions(5);

  const loadRankingData = useCallback(() => {
    const { fromDate, toDate } = getMonthDateRange(selectedMonth, selectedYear);
    setLoading(true);
    setError(null);

    dispatch(fetchLabUtilizationRanking({ companyId: companyID, fromDate, toDate }))
      .unwrap()
      .then((result) => {
        if (Array.isArray(result)) setData(result);
        else if (Array.isArray(result?.data)) setData(result.data);
        else if (Array.isArray(result?.$values)) setData(result.$values);
        else setData([]);
      })
      .catch((err) => setError(err?.message || "Failed to load data."))
      .finally(() => setLoading(false));
  }, [dispatch, selectedMonth, selectedYear]);

  useEffect(() => {
    loadRankingData();
  }, [loadRankingData]);

  useDashboardSignalR(loadRankingData);

  // Sort descending by UtilizationPct, take top 10
  const sorted = Array.isArray(data)
    ? [...data].sort((a, b) => b.UtilizationPct - a.UtilizationPct).slice(0, 10)
    : [];

  const categories = sorted.map((d) => String(d.FacilityName));
  const seriesData = sorted.map((d) => parseFloat(d.UtilizationPct) || 0);
  const colors = sorted.map((_, i) => BAR_COLORS[i % BAR_COLORS.length]);

  const chartOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
        export: {
          csv: {
            filename: "Lab_Utilization_Ranking",
            columnDelimiter: ",",
            headerCategory: "Facility",
            headerValue: "Utilization %",
          },
          svg: { filename: "Lab_Utilization_Ranking" },
          png: { filename: "Lab_Utilization_Ranking" },
        },
      },
      animations: { enabled: true, speed: 600 },
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    },
    plotOptions: {
      bar: {
        horizontal: true,
        distributed: true,
        barHeight: "60%",
        dataLabels: { position: "end" },
        borderRadius: 6,
      },
    },
    colors: colors,
    dataLabels: {
      enabled: true,
      textAnchor: "end",
      style: {
        fontSize: "13px",
        fontWeight: 700,
        colors: ["#1f2937"],
      },
      formatter: function (val) {
        return val.toFixed(1) + "%";
      },
      offsetX: 10,
      dropShadow: { enabled: false },
    },
    stroke: {
      width: 0,
      colors: ["transparent"],
    },
    xaxis: {
      categories: categories.length > 0 ? categories : ["No Data"],
      min: 0,
      max: 100,
      labels: {
        formatter: (val) =>
          val !== undefined && val !== "No Data" ? val + "%" : "",
        style: { fontSize: "11px", fontWeight: 500, colors: "#64748b" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        show: true,
        style: {
          fontSize: "13px",
          fontWeight: 600,
          colors: "#1f2937",
        },
        maxWidth: 150,
        offsetX: -10,
      },
    },
    grid: {
      borderColor: "transparent",
      xaxis: { lines: { show: true, strokeDashArray: 4 } },
      yaxis: { lines: { show: false } },
      padding: { top: 0, right: 20, bottom: 0, left: 20 },
    },
    legend: { show: false },
    tooltip: {
      enabled: true,
      theme: "light",
      x: { show: true },
      y: {
        title: { formatter: () => "" },
        formatter: (val, { dataPointIndex }) => {
          const lab = sorted[dataPointIndex];
          return [
            `<strong style="color: #1f2937; font-size: 13px;">Utilization: ${val.toFixed(1)}%</strong>`,
            `<span style="color: #475569; font-size: 12px;">Total: ${lab?.TotalHours ?? "-"} hrs</span>`,
            `<span style="color: #475569; font-size: 12px;">Billable: ${lab?.BillableHours ?? "-"} hrs</span>`,
            `<span style="color: #475569; font-size: 12px;">Non-Billable: ${lab?.NonBillableHours ?? "-"} hrs</span>`,
          ].join("<br/>");
        },
      },
      style: {
        fontSize: "12px",
        backgroundColor: "#ffffff",
        borderColor: "#e5e7eb",
        borderRadius: "8px",
        padding: "8px 12px",
      },
      onDatasetHover: { highlightDataSeries: true },
    },
  };

  const chartSeries = [{ name: "Utilization", data: seriesData }];

  return (
    <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-6 hover:shadow-lg transition-shadow duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-6 border-b border-slate-200">
        <div>
          <h3 className="text-xl font-bold text-slate-900 tracking-tight">Laboratory Utilization Ranking</h3>
          <p className="text-sm text-slate-500 mt-1">Top labs by utilization percentage</p>
        </div>
        <div className="flex gap-3">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08757C]/50 transition-all"
          >
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08757C]/50 transition-all"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-12 h-12 rounded-full border-4 border-slate-200 border-t-[#08757C] animate-spin mb-4"></div>
          <p className="text-slate-600 font-medium">Loading utilization data...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-12 bg-red-50 rounded-xl border border-red-200">
          <span className="text-4xl mb-3">⚠️</span>
          <p className="text-red-700 font-medium">Failed to load data. Please try again.</p>
        </div>
      ) : sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-slate-50 rounded-xl border border-slate-200">
          <span className="text-4xl mb-3">📊</span>
          <p className="text-slate-600 font-medium">No utilization data found for this period.</p>
        </div>
      ) : (
        <>
          {/* Summary Pills */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            {sorted.slice(0, 3).map((lab, i) => (
              <div
                key={lab.FacilityId}
                className="flex items-center gap-4 p-4 rounded-xl border-2 bg-gradient-to-br from-white to-slate-50 hover:shadow-md transition-all"
                style={{ borderColor: BAR_COLORS[i] }}
              >
                <div
                  className="flex items-center justify-center h-10 w-10 rounded-full text-white font-bold text-sm flex-shrink-0"
                  style={{ backgroundColor: BAR_COLORS[i] }}
                >
                  #{i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{lab.FacilityName}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Utilization</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold" style={{ color: BAR_COLORS[i] }}>
                    {lab.UtilizationPct}%
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Chart */}
          {sorted.length > 0 && categories.length > 0 && (
            <div className="mb-6 overflow-visible">
              <ReactApexChart
                key={categories.join(",")}
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={Math.max(380, sorted.length * 48)}
              />
            </div>
          )}

          {/* Footer Legend */}
          <div className="pt-6 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#4f86f7" }}></div>
              <span className="text-sm text-slate-600">Billable hours counted toward utilization</span>
            </div>
            <div className="text-sm text-slate-600">
              Total Labs: <span className="font-bold text-slate-900">{sorted.length}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}